import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, resolveComponent as _resolveComponent, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "grid h-screen grid-rows-[auto_minmax(0,1fr)] overflow-hidden bg-slate-100 text-slate-950 dark:bg-slate-950 dark:text-slate-50 md:grid-cols-[240px_minmax(0,1fr)] md:grid-rows-1" }
const _hoisted_2 = { class: "grid min-h-0 min-w-0 grid-rows-[3.5rem_minmax(0,1fr)]" }
const _hoisted_3 = { class: "min-h-0 min-w-0 overflow-y-auto" }

import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useApiClient } from "@/application/api";
import { useWebSocketClient } from "@/application/realtime";
import { useStore } from "@/application/store";
import LogoutModal from "./components/LogoutModal.vue";
import MainHeader from "./components/MainHeader.vue";
import MainSidebar from "./components/MainSidebar.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'MainLayout',
  setup(__props) {

const router = useRouter();
const store = useStore();
const apiClient = useApiClient();
const webSocketClient = useWebSocketClient();

const isLogoutModalOpen = ref(false);
const userName = computed(
  () =>
    store.state.authorization.user?.fullName ||
    store.state.authorization.user?.login ||
    ""
);
const canViewSystem = computed(
  () => hasAnyPermission(["system.read"]) || hasRole("superadministrator")
);
const canViewOperatingRooms = computed(
  () =>
    hasAnyPermission(["operating_rooms.read"]) || hasRole("superadministrator")
);
const canViewAdverseEvents = computed(
  () =>
    hasAnyPermission([
      "adverse_events.read",
      "adverse_event_dictionaries.manage",
    ]) || hasRole("superadministrator")
);
const canManageDepartments = computed(
  () =>
    hasAnyPermission(["departments.manage"]) || hasRole("superadministrator")
);
const canManageNotificationSettings = computed(
  () =>
    hasAnyPermission(["notification_settings.manage"]) ||
    hasRole("superadministrator")
);
const canViewUsers = computed(
  () =>
    hasAnyPermission(["users.manage", "roles.manage"]) ||
    hasRole("superadministrator")
);
const unreadNotifications = computed(
  () => store.state.notifications.unreadCount
);
let unsubscribeNotifications: (() => void) | null = null;

onMounted(() => {
  webSocketClient.connect();
  apiClient.notifications.list(1, 0).then((result) => {
    store.commit("notifications/setUnreadCount", result.unreadCount);
  }).catch(() => undefined);
  unsubscribeNotifications =
    webSocketClient.on<iSharedNotifications.NotificationDto>(
      "notification:created",
      (notification) => {
        store.commit("notifications/addNotification", notification);
      }
    );
});

onUnmounted(() => {
  webSocketClient.disconnect();
  unsubscribeNotifications?.();
});

function openLogoutModal(): void {
  isLogoutModalOpen.value = true;
}

function logout(): void {
  isLogoutModalOpen.value = false;
  apiClient.authorization.logout().then(() => {
    webSocketClient.disconnect();
    store.commit("authorization/clearUser");
    store.commit("notifications/clear");
    router.replace({ name: "login" });
  });
}

function hasPermission(
  permissionKey: iSharedPermission.PermissionKey
): boolean {
  return Boolean(
    store.state.authorization.user?.permissions.some(
      (permission) => permission.key === permissionKey
    )
  );
}

function hasAnyPermission(
  permissionKeys: iSharedPermission.PermissionKey[]
): boolean {
  return permissionKeys.some((permissionKey) => hasPermission(permissionKey));
}

function hasRole(roleName: iSharedUserRole.UserRoleName): boolean {
  return Boolean(
    store.state.authorization.user?.roles.some((role) => role.name === roleName)
  );
}

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(MainSidebar, {
      "can-view-system": canViewSystem.value,
      "can-view-operating-rooms": canViewOperatingRooms.value,
      "can-view-adverse-events": canViewAdverseEvents.value,
      "can-manage-departments": canManageDepartments.value,
      "can-manage-notification-settings": canManageNotificationSettings.value,
      "can-view-users": canViewUsers.value
    }, null, 8, ["can-view-system", "can-view-operating-rooms", "can-view-adverse-events", "can-manage-departments", "can-manage-notification-settings", "can-view-users"]),
    _createElementVNode("section", _hoisted_2, [
      _createVNode(MainHeader, {
        "user-name": userName.value,
        "unread-notifications": unreadNotifications.value,
        onLogout: openLogoutModal
      }, null, 8, ["user-name", "unread-notifications"]),
      _createElementVNode("main", _hoisted_3, [
        _createVNode(_component_router_view)
      ])
    ]),
    _createVNode(LogoutModal, {
      modelValue: isLogoutModalOpen.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((isLogoutModalOpen).value = $event)),
      onConfirm: logout
    }, null, 8, ["modelValue"])
  ]))
}
}

})